import { Search } from 'lucide-react';
import { FormField, SearchField, type SearchFieldRef } from '@/components/custom/erp';
import { accountSearchColumns, checkboxColumn, QUERY_KEYS } from '@/constants';
import type { TaiKhoan } from '@/types/schemas';
import { Label } from '@/components/ui';

interface GeneralTabProps {
  mode: 'add' | 'edit' | 'view';
  searchFieldStates: {
    taiKhoan: TaiKhoan[];
    setTaiKhoan: React.Dispatch<React.SetStateAction<TaiKhoan[]>>;
    taiKhoanDoiUng: TaiKhoan[];
    setTaiKhoanDoiUng: React.Dispatch<React.SetStateAction<TaiKhoan[]>>;
    taiKhoanGiamTru: TaiKhoan[];
    setTaiKhoanGiamTru: React.Dispatch<React.SetStateAction<TaiKhoan[]>>;
  };
  accountRef?: React.RefObject<SearchFieldRef<TaiKhoan>>;
}

export const GeneralTab = ({ mode, searchFieldStates, accountRef }: GeneralTabProps) => {
  const disabled = mode === 'view';
  const handleCellValueChange = (rowData: any, field: string, newValue: any) => {
    if (field === 'selected') {
      const invoiceUuid = rowData.code;
      if (newValue) {
        searchFieldStates.setTaiKhoan((prev: any[] | null) => {
          const prevArray = Array.isArray(prev) ? prev : [];
          return [...prevArray, rowData];
        });
      } else {
        searchFieldStates.setTaiKhoan((prev: any[] | null) => {
          const prevArray = Array.isArray(prev) ? prev : [];
          return prevArray.filter((invoice: any) => invoice.code !== invoiceUuid);
        });
      }
    }
  };
  const handleCellValueChangeDoiUng = (rowData: TaiKhoan, field: string, newValue: TaiKhoan) => {
    if (field === 'selected') {
      const invoiceUuid = rowData.code;
      if (newValue) {
        searchFieldStates.setTaiKhoanDoiUng((prev: TaiKhoan[] | null) => {
          const prevArray = Array.isArray(prev) ? prev : [];
          return [...prevArray, rowData];
        });
      } else {
        searchFieldStates.setTaiKhoanDoiUng((prev: TaiKhoan[] | null) => {
          const prevArray = Array.isArray(prev) ? prev : [];
          return prevArray.filter((invoice: TaiKhoan) => invoice.code !== invoiceUuid);
        });
      }
    }
  };
  const handleCellValueChangeGiamTru = (rowData: TaiKhoan, field: string, newValue: TaiKhoan) => {
    if (field === 'selected') {
      const invoiceUuid = rowData.code;
      if (newValue) {
        searchFieldStates.setTaiKhoanGiamTru((prev: TaiKhoan[] | null) => {
          const prevArray = Array.isArray(prev) ? prev : [];
          return [...prevArray, rowData];
        });
      } else {
        searchFieldStates.setTaiKhoanGiamTru((prev: TaiKhoan[] | null) => {
          const prevArray = Array.isArray(prev) ? prev : [];
          return prevArray.filter((invoice: TaiKhoan) => invoice.code !== invoiceUuid);
        });
      }
    }
  };
  return (
    <div className='h-[330px] space-y-1 px-4 py-1'>
      {/* Loại dữ liệu */}
      <div className='flex items-center'>
        <Label className='mt-2 w-40 min-w-40 font-medium'>Loại dữ liệu</Label>
        <FormField
          name='loai'
          type='select'
          options={[
            { value: '0', label: 'Lấy theo mã số hoặc lập công thức' },
            { value: '1', label: 'Lấy số dư tài khoản' },
            { value: '2', label: 'Lấy phát sinh tài khoản sổ cái' },
            { value: '3', label: 'Lấy phát sinh tài khoản giá thành vụ việc' },
            { value: '4', label: 'Lấy phát sinh tài khoản sổ cái và giá thành vụ việc' }
          ]}
          className='min-w-[350px]'
          disabled={disabled}
        />
      </div>

      {/* Loại phát sinh */}
      <div className='flex items-center'>
        <Label className='mt-2 w-40 min-w-40 font-medium'>Loại phát sinh</Label>
        <FormField
          name='no_co'
          type='select'
          className='min-w-[350px]'
          options={[
            { value: '1', label: 'Nợ' },
            { value: '2', label: 'Có' },
            { value: '3', label: 'Nợ trừ Có' },
            { value: '4', label: 'Có trừ Nợ' }
          ]}
        />
      </div>

      {/* Thời điểm */}
      <div className='flex items-center'>
        <Label className='mt-2 w-40 min-w-40 font-medium'>Thời điểm</Label>
        <FormField
          name='dau_cuoi'
          className='min-w-[350px]'
          type='select'
          options={[
            { label: 'Đầu kỳ', value: '1' },
            { label: 'Cuối kỳ', value: '2' }
          ]}
          disabled={disabled}
        />
      </div>

      {/* Loại */}
      <div className='flex items-center'>
        <Label className='mt-2 w-40 min-w-40 font-medium'>Loại</Label>
        <FormField
          name='cong_no'
          className='min-w-[350px]'
          type='select'
          options={[
            { label: 'Không lấy chi tiết 1 vế của đối tượng công nợ', value: '0' },
            { label: 'Lấy chi tiết 1 vế của đối tượng công nợ', value: '1' }
          ]}
          disabled={disabled}
        />
      </div>

      {/* Giá trị */}
      <div className='flex items-center'>
        <Label className='mt-2 w-40 min-w-40 font-medium'>Giá trị</Label>
        <FormField
          name='dau'
          className='min-w-[350px]'
          type='select'
          options={[
            { label: 'Bình thường', value: '0' },
            { label: 'Không âm', value: '1' },
            { label: 'Đảo dấu', value: '2' }
          ]}
          disabled={disabled}
        />
      </div>

      {/* Tài khoản */}
      <div className='flex items-center'>
        <Label className='mt-2 w-40 min-w-40 font-medium'>Tài khoản</Label>
        <SearchField<TaiKhoan>
          ref={accountRef}
          multiple
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN_ALL}`}
          searchColumns={[
            ...checkboxColumn(handleCellValueChange, searchFieldStates.taiKhoan, 'code'),
            ...accountSearchColumns
          ]}
          columnDisplay='code'
          displayRelatedField='name'
          dialogTitle='Danh mục tài khoản'
          value={searchFieldStates.taiKhoan}
          onRowSelection={searchFieldStates.setTaiKhoan}
          className='min-w-[350px]'
        />
      </div>

      {/* Tài khoản đối ứng */}
      <div className='flex items-center'>
        <Label className='mt-2 w-40 min-w-40 font-medium'>Tài khoản đối ứng</Label>
        <SearchField<TaiKhoan>
          ref={accountRef}
          multiple
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN_ALL}`}
          searchColumns={[
            ...checkboxColumn(handleCellValueChangeDoiUng, searchFieldStates.taiKhoanDoiUng, 'code'),
            ...accountSearchColumns
          ]}
          columnDisplay='code'
          displayRelatedField='name'
          dialogTitle='Danh mục tài khoản'
          value={searchFieldStates.taiKhoanDoiUng}
          onRowSelection={searchFieldStates.setTaiKhoanDoiUng}
          className='min-w-[350px]'
        />
      </div>

      {/* Tài khoản giảm trừ */}
      <div className='flex items-center'>
        <Label className='mt-2 w-40 min-w-40 font-medium'>Tài khoản giảm trừ</Label>
        <SearchField<TaiKhoan>
          ref={accountRef}
          multiple
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN_ALL}`}
          searchColumns={[
            ...checkboxColumn(handleCellValueChangeGiamTru, searchFieldStates.taiKhoanGiamTru, 'code'),
            ...accountSearchColumns
          ]}
          columnDisplay='code'
          displayRelatedField='name'
          dialogTitle='Danh mục tài khoản'
          value={searchFieldStates.taiKhoanGiamTru}
          onRowSelection={searchFieldStates.setTaiKhoanGiamTru}
          className='min-w-[350px]'
        />
      </div>

      {/* Công thức */}
      <div className='flex items-center'>
        <Label className='mt-2 w-40 min-w-40 font-medium'>Công thức</Label>
        <div className='flex-1'>
          <FormField name='cong_thuc' />
        </div>
      </div>
    </div>
  );
};
